import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { IconCheck, IconChevronDown } from './NavIcons';

export type SelectOption<V extends string = string> = {
  value: V;
  label: string;
  description?: string;
  /** Short right-aligned tag, e.g. a code or count */
  meta?: string;
  /** Leading visual, e.g. a flag or avatar initial */
  leading?: ReactNode;
  group?: string;
  disabled?: boolean;
};

type Props<V extends string> = {
  value: V | '' | null | undefined;
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: string;
  disabled?: boolean;
  /** Defaults to on when there are more than 7 options */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  invalid?: boolean;
  size?: 'md' | 'sm';
  id?: string;
  className?: string;
  'aria-label'?: string;
};

export default function Select<V extends string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  searchable,
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  invalid,
  size = 'md',
  id,
  className = '',
  'aria-label': ariaLabel,
}: Props<V>) {
  const autoId = useId();
  const listId = `${id || autoId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const canSearch = searchable ?? options.length > 7;
  const selected = options.find((o) => o.value === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => [o.label, o.description, o.meta, o.value].some((t) => t && t.toLowerCase().includes(q)));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setDropUp(window.innerHeight - rect.bottom < 320 && rect.top > 320);
    const idx = filtered.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx : Math.max(0, filtered.findIndex((o) => !o.disabled)));
    if (canSearch) requestAnimationFrame(() => searchRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  useEffect(() => {
    if (open) setActive(Math.max(0, filtered.findIndex((o) => !o.disabled)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (o: SelectOption<V> | undefined) => {
    if (!o || o.disabled) return;
    onChange(o.value);
    close();
  };

  const move = (dir: 1 | -1) => {
    if (!filtered.length) return;
    let i = active;
    for (let n = 0; n < filtered.length; n++) {
      i = (i + dir + filtered.length) % filtered.length;
      if (!filtered[i].disabled) break;
    }
    setActive(i);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(filtered.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const pad = size === 'sm' ? 'px-3 py-2' : 'px-3.5 py-2.5';
  const border = invalid ? '#e11d48' : open ? '#d6006e' : '#f9c0d8';

  let lastGroup: string | undefined;

  return (
    <div ref={rootRef} className={`relative ${className}`} onKeyDown={onKeyDown}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        className={`flex w-full items-center gap-2.5 rounded-xl text-left text-sm transition-shadow disabled:cursor-not-allowed disabled:opacity-60 ${pad}`}
        style={{
          border: `1.5px solid ${border}`,
          background: disabled ? '#fbf3f6' : '#fff5f8',
          color: '#6b0030',
          boxShadow: open ? '0 0 0 3px rgba(214,0,110,0.14)' : undefined,
        }}
      >
        {selected?.leading && <span className="flex shrink-0 items-center">{selected.leading}</span>}
        <span className="min-w-0 flex-1">
          {selected ? (
            <span className="block truncate font-semibold">{selected.label}</span>
          ) : (
            <span className="block truncate text-[#c084a0]">{placeholder}</span>
          )}
        </span>
        {selected?.meta && size === 'md' && (
          <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{selected.meta}</span>
        )}
        <IconChevronDown className={`h-4 w-4 shrink-0 text-[#d6006e] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={`mz-pop absolute left-0 right-0 z-50 overflow-hidden rounded-2xl bg-white ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}
          style={{ border: '1px solid #fce7f3', boxShadow: '0 24px 60px rgba(107,0,48,0.18)', minWidth: 220 }}
        >
          {canSearch && (
            <div className="border-b border-[#fce7f3] p-2">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg bg-[#fff5f8] px-3 py-2 text-sm text-[#6b0030] outline-none placeholder:text-[#c084a0] focus:ring-2 focus:ring-[#d6006e]/20"
                aria-controls={listId}
                aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
              />
            </div>
          )}
          <ul ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto p-1.5" tabIndex={-1}>
            {filtered.length === 0 && <li className="px-3 py-6 text-center text-sm text-[#c084a0]">{emptyText}</li>}
            {filtered.map((o, i) => {
              const header = o.group && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group;
              const isSel = o.value === value;
              const isActive = i === active;
              return (
                <li key={o.value} role="presentation">
                  {header && (
                    <p className="px-3 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#c084a0]">{header}</p>
                  )}
                  <div
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={isSel}
                    aria-disabled={o.disabled || undefined}
                    data-index={i}
                    onMouseEnter={() => !o.disabled && setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(o)}
                    className={`flex cursor-pointer items-start gap-2.5 rounded-xl px-3 py-2 transition-colors ${
                      o.disabled ? 'cursor-not-allowed opacity-45' : isActive ? 'bg-[#fdf2f8]' : ''
                    }`}
                  >
                    {o.leading && <span className="mt-0.5 flex shrink-0 items-center">{o.leading}</span>}
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${isSel ? 'font-bold text-[#d6006e]' : 'font-semibold text-[#6b0030]'}`}>{o.label}</span>
                      {o.description && <span className="mt-0.5 block text-xs leading-snug text-[#b0708f]">{o.description}</span>}
                    </span>
                    {o.meta && (
                      <span className="mt-0.5 shrink-0 rounded-md bg-[#fdf2f8] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">
                        {o.meta}
                      </span>
                    )}
                    <IconCheck className={`mt-0.5 h-4 w-4 shrink-0 text-[#d6006e] ${isSel ? '' : 'invisible'}`} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
